import { FileDownload } from "@mui/icons-material";
import {
    Box,
    Grid,
    IconButton,
    LinearProgress,
    Paper,
    TableBody,
    TableCell,
    TableHead,
} from "@mui/material";
import { useSuspenseQuery } from "@tanstack/react-query";
import { createFileRoute } from "@tanstack/react-router";
import { asBlob, generateCsv, mkConfig } from "export-to-csv";
import { useCallback, useEffect, useState } from "react";
import PageHeader from "@/components/PageHeader";
import DataTable, {
    formatDateStandard,
    StyledHeaderTableRow,
    StyledPrimaryTableCell,
    StyledSecondaryTableCell,
    StyledTableRow,
    StyledTableSortLabel,
} from "@/components/Table/DataTable.tsx";
import SearchSection from "@/components/Table/SearchSection.tsx";
import { useQueryOptionsFactory } from "@/queries";
import type { ListReservation } from "@/queries/reservations.ts";

export const Route = createFileRoute("/admin/guests/")({
    component: RouteComponent,
    pendingComponent: LinearProgress,
    loader: async ({ context }) => {
        await context.queryClient.ensureQueryData(context.qof.reservations.list());
    },
});

type SortKeys = keyof Pick<
    ListReservation,
    "rentalId" | "startDate" | "endDate" | "customerName" | "confirmationNumber"
>;

const csvConfig = mkConfig({
    useKeysAsHeaders: true,
    filename: "guestReport.csv",
});

function RouteComponent() {
    const qof = useQueryOptionsFactory();
    const pageData: { data: ListReservation[] } = useSuspenseQuery(qof.reservations.list()).data;

    const [sortField, setSortField] = useState<SortKeys>("customerName");
    const [sortDirection, setSortDirection] = useState<"asc" | "desc">("asc");
    const [searchTerm, setSearchTerm] = useState("");
    const [displayedData, setDisplayedData] = useState<ListReservation[]>([]);
    const [isLoading, setIsLoading] = useState(false);
    const [hasMore, setHasMore] = useState(true);
    const [page, setPage] = useState(0);
    const ITEMS_PER_PAGE = 50;

    const handleSort = (field: SortKeys) => {
        if (sortField === field) {
            setSortDirection(sortDirection === "asc" ? "desc" : "asc");
        } else {
            setSortField(field);
            setSortDirection(field === "startDate" || field === "endDate" ? "desc" : "asc");
        }
        setPage(0);
        setDisplayedData([]);
        setHasMore(true);
    };

    const getSortedAndFilteredData = useCallback(() => {
        const filteredData = pageData.data.filter((reservation) => {
            const searchLower = searchTerm.toLowerCase();
            return (
                reservation.confirmationNumber.toLowerCase().includes(searchLower) ||
                reservation.rentalId.toLowerCase().includes(searchLower) ||
                reservation.customerFirstName.toLowerCase().includes(searchLower) ||
                reservation.customerName.toLowerCase().includes(searchLower) ||
                // reservation.startDate.toLocaleDateString().toLowerCase().includes(searchLower) ||
                formatDateStandard(reservation.startDate)
                    .toLowerCase()
                    .includes(searchLower) ||
                formatDateStandard(reservation.endDate).toLowerCase().includes(searchLower)
                // reservation.endDate.toLocaleDateString().toLowerCase().includes(searchLower)
            );
        });

        return filteredData.sort((a, b) => {
            let aValue: string | Date | number | undefined = a[sortField];
            let bValue: string | Date | number | undefined = b[sortField];

            if (sortField === "startDate" || sortField === "endDate") {
                aValue = (aValue instanceof Date && aValue.getTime()) || 0;
                bValue = (bValue instanceof Date && bValue.getTime()) || 0;
            } else if (sortField === "rentalId") {
                aValue = Number.parseInt(aValue?.toString() ?? "0");
                bValue = Number.parseInt(bValue?.toString() ?? "0");
            }

            if (aValue < bValue) {
                return sortDirection === "asc" ? -1 : 1;
            }
            if (aValue > bValue) {
                return sortDirection === "asc" ? 1 : -1;
            }
            return 0;
        });
    }, [searchTerm, sortField, sortDirection, pageData.data]);

    const loadMoreData = useCallback(async () => {
        if (isLoading || !hasMore) {
            return;
        }

        setIsLoading(true);

        const sortedAndFilteredData = getSortedAndFilteredData();
        const startIndex = page * ITEMS_PER_PAGE;
        const endIndex = startIndex + ITEMS_PER_PAGE;
        const newData = sortedAndFilteredData.slice(startIndex, endIndex);

        if (newData.length === 0 || endIndex >= sortedAndFilteredData.length) {
            setHasMore(false);
        }

        if (page === 0) {
            setDisplayedData(newData);
        } else {
            setDisplayedData((prev) => [...prev, ...newData]);
        }

        setPage((prev) => prev + 1);
        setIsLoading(false);
    }, [page, isLoading, hasMore, getSortedAndFilteredData]);

    // do not remove hook deps... they are necessary for functionality and this rule is dumb
    useEffect(() => {
        setPage(0);
        setDisplayedData([]);
        setHasMore(true);
    }, [searchTerm, sortField, sortDirection]);

    useEffect(() => {
        if (displayedData.length === 0 && hasMore) {
            loadMoreData();
        }
    }, [displayedData.length, hasMore, loadMoreData]);

    const download = async () => {
        const filename = "guestReport.csv";
        const data = getSortedAndFilteredData().map((reservation) => ({
            id: reservation.id,
            confirmationNumber: reservation.confirmationNumber,
            rentalId: reservation.rentalId,
            customerFirstName: reservation.customerFirstName,
            customerName: reservation.customerName,
            startDate: reservation.startDate.toLocaleDateString(),
            endDate: reservation.endDate.toLocaleDateString(),
        }));
        const output = generateCsv(csvConfig)(data);
        const blob = asBlob(csvConfig)(output);
        const url = URL.createObjectURL(blob);

        const a = document.createElement("a");
        a.href = url;
        a.download = filename;
        a.click();
        console.log("click");

        URL.revokeObjectURL(url);
    };

    return (
        <Box sx={{ maxWidth: "100%" }}>
            <Paper elevation={2} sx={{ borderRadius: 3, overflow: "hidden" }}>
                <PageHeader
                    title="Guests Report - Comprehensive Guest Analytics"
                    instructions={[
                        "View guest reservations and booking details",
                        "Search by confirmation number, unit number, guest name, or dates",
                        "Sort by any column to organize reservation data",
                        "Track check-in and check-out dates for all guests",
                    ]}
                />
                <Grid container direction="row" sx={{ alignItems: "stretch" }}>
                    <Grid sx={{ flexGrow: 1 }}>
                        <SearchSection
                            searchTerm={searchTerm}
                            onSearchChange={setSearchTerm}
                            onClearSearch={() => setSearchTerm("")}
                            placeholder="Search by confirmation #, unit number, guest name, or dates..."
                            displayedCount={displayedData.length}
                            totalCount={getSortedAndFilteredData().length}
                            totalAllCount={pageData.data.length}
                        />
                    </Grid>
                    <Grid>
                        <Box
                            sx={{
                                p: { xs: 1.5, sm: 2 },
                                borderBottom: "1px solid #e2e8f0",
                                backgroundColor: "#f8fafc",
                                height: "100%",
                                justifyContent: "flex-end",
                                textAlign: "right",
                            }}
                        >
                            <IconButton
                                aria-label="Download"
                                onClick={() => {
                                    download();
                                }}
                            >
                                <FileDownload />
                            </IconButton>
                        </Box>
                    </Grid>
                </Grid>

                <DataTable
                    isLoading={isLoading}
                    hasMore={hasMore}
                    onLoadMore={loadMoreData}
                    isEmpty={displayedData.length === 0}
                    emptyMessage={
                        searchTerm
                            ? "No guests found matching your search."
                            : "No guest data available."
                    }
                    colSpan={5}
                    maxHeight={{ xs: "600px", sm: "600px" }}
                    loadingMessage="Loading more reservations..."
                    endMessage="No more reservations to load"
                >
                    <TableHead>
                        <StyledHeaderTableRow>
                            <TableCell sx={{ minWidth: { xs: "100px", sm: "140px" } }}>
                                <StyledTableSortLabel
                                    active={sortField === "confirmationNumber"}
                                    direction={
                                        sortField === "confirmationNumber" ? sortDirection : "asc"
                                    }
                                    onClick={() => handleSort("confirmationNumber")}
                                >
                                    Confirmation #
                                </StyledTableSortLabel>
                            </TableCell>

                            <TableCell sx={{ minWidth: { xs: "60px", sm: "80px" } }}>
                                <StyledTableSortLabel
                                    active={sortField === "rentalId"}
                                    direction={sortField === "rentalId" ? sortDirection : "asc"}
                                    onClick={() => handleSort("rentalId")}
                                >
                                    Unit No.
                                </StyledTableSortLabel>
                            </TableCell>

                            <TableCell sx={{ minWidth: { xs: "120px", sm: "160px" } }}>
                                <StyledTableSortLabel
                                    active={sortField === "customerName"}
                                    direction={sortField === "customerName" ? sortDirection : "asc"}
                                    onClick={() => handleSort("customerName")}
                                >
                                    Guest Name
                                </StyledTableSortLabel>
                            </TableCell>

                            <TableCell sx={{ minWidth: { xs: "80px", sm: "100px" } }}>
                                <StyledTableSortLabel
                                    active={sortField === "startDate"}
                                    direction={sortField === "startDate" ? sortDirection : "asc"}
                                    onClick={() => handleSort("startDate")}
                                >
                                    Check-in
                                </StyledTableSortLabel>
                            </TableCell>

                            <TableCell sx={{ minWidth: { xs: "80px", sm: "100px" } }}>
                                <StyledTableSortLabel
                                    active={sortField === "endDate"}
                                    direction={sortField === "endDate" ? sortDirection : "asc"}
                                    onClick={() => handleSort("endDate")}
                                >
                                    Check-out
                                </StyledTableSortLabel>
                            </TableCell>
                        </StyledHeaderTableRow>
                    </TableHead>
                    <TableBody>
                        {displayedData.map((reservation, index) => (
                            <StyledTableRow
                                key={reservation.id}
                                sx={{
                                    backgroundColor: index % 2 === 0 ? "#ffffff" : "#f8fafc",
                                }}
                            >
                                <StyledPrimaryTableCell
                                    sx={{
                                        fontFamily: "monospace",
                                        fontSize: { xs: "0.75rem", sm: "0.85rem" },
                                    }}
                                >
                                    {reservation.confirmationNumber}
                                </StyledPrimaryTableCell>

                                <StyledPrimaryTableCell>
                                    {reservation.rentalId}
                                </StyledPrimaryTableCell>

                                <StyledPrimaryTableCell>
                                    {reservation.customerFirstName} {reservation.customerName}
                                </StyledPrimaryTableCell>

                                <StyledSecondaryTableCell>
                                    {formatDateStandard(reservation.startDate)}
                                </StyledSecondaryTableCell>

                                <StyledSecondaryTableCell>
                                    {formatDateStandard(reservation.endDate)}
                                </StyledSecondaryTableCell>
                            </StyledTableRow>
                        ))}
                    </TableBody>
                </DataTable>
            </Paper>
        </Box>
    );
}
